import { Box, Divider, makeStyles, Theme, Typography, useMediaQuery } from "@material-ui/core";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import CustomCard from "../../../components/CustomCard";
import ErrorCatcher from "../../../components/ErrorCatcher";
import ImagePreviewer from "../../../components/ImagePreviewer";
import Loader from "../../../components/Loader";
import { GenericObject } from "../../../types/generic.types";
import { clientDocRef, orderDocRef } from "../../../utils/referencies";
import ExpectedDeliveryDate from "./ExpectedDeliveryDate";
import OrdersStatusText from "./OrdersStatusText";
import TitledText from "./TitledText";

const useStyles = makeStyles(() => ({
  flexBox: {
    display: "flex",
    alignItems: "center",
    marginBottom: 18,
    paddingBottom: 18,
    borderBottom: "1px solid #e0e0e0",
  },
  boxTitle: {
    minWidth: "300px",
  },
  imgStyle: {
    width: "150px",
  },
  boxTitleRespons: {
    width: "100px",
    minWidth: "100px",
  },
}));

const OrderDetails = () => {
  const [fetchedClient, setClientData] = useState<GenericObject | undefined>(undefined);
  const [fetchedOrder, setFetchedOrder] = useState<GenericObject | undefined>(undefined);
  const [fetchedCartItems, setFetchedCartItems] = useState<GenericObject[]>([]);
  const [loadingOrderData, setLoadingOrderData] = useState(false);
  const [loadingClientData, setLoadingClientData] = useState(false);

  const { orderId } = useParams<{ orderId: string }>();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  /**
   * this function to get order details
   */
  const getOrderData = async () => {
    setLoadingOrderData(true);
    try {
      const snapshot = await orderDocRef(orderId).get();
      if (snapshot.exists) {
        const fetchedOrder: GenericObject = {
          ...snapshot.data(),
          id: snapshot.id,
        };
        setFetchedOrder(fetchedOrder);
        setFetchedCartItems(fetchedOrder.items);
        setLoadingOrderData(false);
      }
    } catch (ex: any) {
      console.log(ex.message);
      setLoadingOrderData(false);
    }
  };

  /**
   * this function to get client data who create order
   */
  const getClientData = async (clientId: string) => {
    setLoadingClientData(true);
    try {
      const snapshot = await clientDocRef(clientId).get();
      setClientData(snapshot.data());
      setLoadingClientData(false);
    } catch (ex: any) {
      console.log(ex.message);
      setLoadingClientData(false);
    }
  };

  /**
   * fetch order data
   */
  useEffect(() => {
    getOrderData();
  }, []);

  /**
   * fetch cart items and client data based on order data
   */
  useEffect(() => {
    if (fetchedOrder) {
      getClientData(fetchedOrder.clientId);
    }
  }, [fetchedOrder]);

  const classes = useStyles();
  return (
    <CustomCard title="Order Details" width="50%">
      <Loader loading={loadingOrderData}>
        {fetchedOrder ? (
          <>
            <Box className={classes.flexBox}>
              <Typography color="textSecondary" className={isSm ? classes.boxTitleRespons : classes.boxTitle}>
                Order
              </Typography>
              <Box>
                <TitledText title="Code" text={fetchedOrder.code} />
                <TitledText title="Payment Method" text={fetchedOrder.paymentMethod} />
                <TitledText title="Delivery Method" text={fetchedOrder.deliveryMethod} />
                <TitledText title="Total Price" text={fetchedOrder.totalPrice} />
                <TitledText
                  title="Created At"
                  text={dayjs(fetchedOrder.createdAt.toDate()).format("DD/MM/YYYY - h:m a")}
                />
              </Box>
            </Box>
            <Box className={classes.flexBox}>
              <Typography color="textSecondary" className={isSm ? classes.boxTitleRespons : classes.boxTitle}>
                Address
              </Typography>

              <Loader loading={loadingOrderData}>
                <Box>
                  <TitledText title="City" text={fetchedOrder.address.city} />
                  <TitledText title="Details" text={fetchedOrder.address.details} />
                  <TitledText title="Notes" text={fetchedOrder.address.notes} />
                </Box>
              </Loader>
            </Box>
            <Box className={classes.flexBox}>
              <Typography color="textSecondary" className={isSm ? classes.boxTitleRespons : classes.boxTitle}>
                Status
              </Typography>
              <Loader loading={loadingOrderData}>
                <OrdersStatusText record={fetchedOrder} source="status" />
              </Loader>
            </Box>
            <Box className={classes.flexBox}>
              <Typography color="textSecondary" className={isSm ? classes.boxTitleRespons : classes.boxTitle}>
                Expected delivery date
              </Typography>
              <Loader loading={loadingOrderData}>
                <ExpectedDeliveryDate record={fetchedOrder} source="expectedDeliveryDate" />
              </Loader>
            </Box>
            <Box className={classes.flexBox}>
              <Typography color="textSecondary" className={isSm ? classes.boxTitleRespons : classes.boxTitle}>
                Client
              </Typography>
              <Loader loading={loadingClientData}>
                {fetchedClient ? (
                  <Box>
                    <TitledText title="Name" text={fetchedClient.displayName} />
                    <TitledText title="Email" text={fetchedClient.email || "N/A"} />
                    <TitledText title="Phone" text={fetchedClient.phoneNumber || "N/A"} />
                  </Box>
                ) : null}
              </Loader>
            </Box>

            <Box className={classes.flexBox}>
              <Typography color="textSecondary" className={isSm ? classes.boxTitleRespons : classes.boxTitle}>
                Products
              </Typography>
              <Loader loading={loadingOrderData}>
                <ErrorCatcher>
                  <Box>
                    {fetchedCartItems.length > 0 ? (
                      <>
                        {fetchedCartItems.map((cartItem) => (
                          <Box mb={2}>
                            <Typography variant="h6">{cartItem.product.title.en}</Typography>
                            <Box mb={1} mx={-2} display="flex" flexWrap="wrap" alignItems="center">
                              <TitledText title="Color" text={cartItem.color} mx={2} />
                              <TitledText title="Price" text={cartItem.product.priceAfterDiscount} mx={2} />
                              <TitledText title="Size" text={cartItem.size} mx={2} />
                              <TitledText title="Quantity" text={cartItem.quantity} mx={2} />
                            </Box>
                            <Box mx={-2} display="flex" alignItems="center">
                              {cartItem.product.properties[cartItem.color].images.map(
                                (imageEntry: { path: string; src: string }) => (
                                  <Box mx={2} mb={1}>
                                    <ImagePreviewer src={imageEntry.src} alt={cartItem.color} />
                                  </Box>
                                )
                              )}
                            </Box>
                            <Divider />
                          </Box>
                        ))}
                      </>
                    ) : (
                      "N/A"
                    )}
                  </Box>
                </ErrorCatcher>
              </Loader>
            </Box>
          </>
        ) : (
          <Typography variant="body1">Order not exist</Typography>
        )}
      </Loader>
    </CustomCard>
  );
};

export default OrderDetails;
